import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Card } from '@/components/ui/card';

interface UsageDataPoint {
  day: string;
  plays: number;
}

interface UsageStatsCardProps {
  usage?: {
    used: number;
    total: number;
    remaining: number;
    percentage: number;
    renew_days: number;
    period_start: string | null;
    period_end: string | null;
    extra_plays: number;
    auto_recharge_active: boolean;
  };
  isLoading: boolean;
  usageData: UsageDataPoint[];
}

interface MicroCardProps {
  label: string;
  value: string | number;
  badge?: 'green' | 'gray';
}

const MicroCard: React.FC<MicroCardProps> = ({ label, value, badge }) => { useLanguage(); return ((
  <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-[10px] p-3 transition-shadow duration-150 hover:shadow-[0px_2px_6px_rgba(0,0,0,0.04)]">
    <p className="text-[12px] text-[#6B7280] mb-0.5">{label}</p>
    {badge ? (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[15px] font-semibold ${
        badge === 'green' 
          ? 'bg-[#EEF4FF] text-[#2563EB]' 
          : 'bg-[#F3F4F6] text-[#6B7280]'
      }`}>
        {value}
      </span>
    ) : (
      <p className="text-[15px] font-semibold text-[#111827]">{value}</p>
    )}
  </div>
)); };

interface MiniCardProps {
  label: string;
  value: string;
}

const MiniCard: React.FC<MiniCardProps> = ({ label, value }) => { useLanguage(); return ((
  <div className="bg-white border border-[#E5E7EB] rounded-[10px] p-4">
    <p className="text-[12px] text-[#6B7280] mb-1">{label}</p>
    <p className="text-[17px] font-semibold text-[#111827]">{value}</p>
  </div>
)); };

export const UsageStatsCard: React.FC<UsageStatsCardProps> = ({
  usage,
  isLoading,
  usageData
}) => {
  useLanguage();
  const remaining = usage?.remaining || 0;
  
  return (
    <Card 
      className="border border-[#E5E7EB] rounded-[14px] overflow-hidden h-full"
      style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}
    >
      <div className="p-7">
        {/* Header */}
        <div className="mb-5">
          <h2 className="text-[18px] font-semibold text-[#111827]">{translateInterfaceText("Uso do Plano")}</h2>
          <p className="text-[13px] text-[#6B7280] mt-0.5">{translateInterfaceText("Acompanhe seu consumo de plays mensais")}</p>
        </div>
        
        {/* BLOCO 1 — Consumo do Plano */}
        <div className="space-y-3">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[18px] font-semibold text-[#111827]">
              {isLoading ? '...' : (usage?.used || 0).toLocaleString()}
            </span>
            <span className="text-[13px] text-[#6B7280]">
              {translateInterfaceText("de")}{" "}{isLoading ? '...' : (usage?.total || 0).toLocaleString()} {" "}{translateInterfaceText("plays utilizados")}</span>
          </div>

          {/* Barra de progresso premium - Padrão Dashboard */}
          <div
            className="h-[10px] rounded-[30px] overflow-hidden"
            style={{ backgroundColor: 'rgba(0,0,0,0.08)' }}
          >
            <div
              className="h-full rounded-[30px] transition-all duration-[400ms] ease-out"
              style={{
                width: `${isLoading ? 0 : usage?.percentage || 0}%`,
                background: '#1F6BFF'
              }}
            />
          </div>
        </div>

        {/* BLOCO 2 — Microcards (Grid 2x2) */}
        <div className="grid grid-cols-2 gap-3 mt-5">
          <MicroCard
            label={translateInterfaceText("Plays restantes")}
            value={`${remaining.toLocaleString()} plays`}
            badge="green"
          />
          <MicroCard
            label={translateInterfaceText("Plays extras adquiridos")}
            value={usage?.extra_plays ? `+${usage.extra_plays.toLocaleString()} plays` : '0 plays'}
          />
          <MicroCard
            label={translateInterfaceText("Início do período")}
            value={usage?.period_start || '—'}
          />
          <MicroCard
            label={translateInterfaceText("Final do período")}
            value={usage?.period_end || '—'}
          />
        </div>

        {/* BLOCO 3 — Limite + Total Consumido */}
        <div className="grid grid-cols-2 gap-4 mt-5">
          <MiniCard
            label={translateInterfaceText("Limite do plano")}
            value={`${(usage?.total || 0).toLocaleString()} plays`}
          />
          <MiniCard
            label={translateInterfaceText("Total consumido")}
            value={`${(usage?.used || 0).toLocaleString()} plays`}
          />
        </div>
      </div>
    </Card>
  );
};
